"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { FormEvent, useState } from "react";
import { site, trackContact, whatsappHref } from "../../lib/site";

type Props = { product?: string };

export default function EnquiryForm({ product = "" }: Props) {
  const router = useRouter();
  const [status, setStatus] = useState<"idle" | "sending" | "fallback">("idle");
  const [notice, setNotice] = useState("");
  const [draft, setDraft] = useState({ name: "", phone: "", company: "", message: "" });

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload = {
      name: String(form.get("name") || ""),
      phone: String(form.get("phone") || ""),
      company: String(form.get("company") || ""),
      message: String(form.get("message") || ""),
      product: String(form.get("product") || ""),
      website: String(form.get("website") || ""),
    };
    setDraft(payload);
    setStatus("sending");
    setNotice("");
    trackContact("form");

    const response = await fetch("/api/enquiry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => null);
    const result = await response?.json().catch(() => null);

    if (response?.ok && result?.ok) {
      router.push("/contact/thank-you");
      return;
    }
    if (result?.reason === "invalid") {
      setStatus("idle");
      setNotice("Please add your name, a phone number, and a short note about the work that is stuck.");
      return;
    }
    if (result?.reason === "rate_limited") {
      setStatus("idle");
      setNotice("Please wait a little while before sending another note, or call us directly.");
      return;
    }
    setStatus("fallback");
  }

  const fallbackText = `Hello LITPL, I am ${draft.name || ""}${draft.company ? ` from ${draft.company}` : ""}. ${draft.phone ? `Phone: ${draft.phone}. ` : ""}${product ? `Product: ${product}. ` : ""}${draft.message}`;

  return (
    <form onSubmit={onSubmit} className="form-shell p-5 sm:p-8" aria-label="Business enquiry">
      <p className="eyebrow mb-3">Send a note</p>
      <h2 className="mb-3 text-2xl font-bold text-slate-900">Tell us what you want to improve</h2>
      <p className="mb-6 text-slate-600">Name, phone, and the task that is slowing the team down are enough to start.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-semibold text-slate-800">
          Name
          <input name="name" required autoComplete="name" className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-3" />
        </label>
        <label className="block text-sm font-semibold text-slate-800">
          Phone
          <input name="phone" required autoComplete="tel" inputMode="tel" className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-3" />
        </label>
        <label className="block text-sm font-semibold text-slate-800 sm:col-span-2">
          Company
          <input name="company" autoComplete="organization" className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-3" />
        </label>
        <label className="block text-sm font-semibold text-slate-800 sm:col-span-2">
          What is stuck?
          <textarea name="message" required minLength={10} rows={5} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-3" placeholder="Billing, stock, leads, vehicles, or a system that no longer fits." />
        </label>
      </div>
      <input type="hidden" name="product" value={product} />
      <div className="hidden" aria-hidden="true">
        <input name="website" tabIndex={-1} autoComplete="off" />
      </div>
      {notice && <p className="mt-4 text-sm text-red-700">{notice}</p>}
      <button type="submit" disabled={status === "sending"} className="primary-action mt-6 disabled:opacity-60">
        {status === "sending" ? "Sending…" : "Send enquiry"}
      </button>
      <p className="mt-4 text-sm text-slate-500">
        Or reach us on <a className="font-semibold text-slate-800 underline underline-offset-4" href={`tel:${site.phoneTel}`} onClick={() => trackContact("call")}>{site.phoneDisplay}</a> or{" "}
        <a className="font-semibold text-slate-800 underline underline-offset-4" href={whatsappHref(product ? `Hello LITPL, I want to discuss ${product}.` : "Hello LITPL, I want to discuss a business requirement.")} target="_blank" rel="noopener noreferrer" onClick={() => trackContact("whatsapp")}>WhatsApp</a>.
        Please read the <Link href="/privacy-policy" className="underline underline-offset-4">privacy policy</Link> before sharing details.
      </p>
      {status === "fallback" && (
        <div className="mt-5 rounded-xl border border-brand-100 bg-brand-50 p-4 text-sm text-slate-700">
          <p>The note could not be emailed from this server. Send the same details directly:</p>
          <div className="mt-3 flex flex-wrap gap-3">
            <a className="primary-action" href={`mailto:${site.email}?subject=${encodeURIComponent("Website enquiry")}&body=${encodeURIComponent(fallbackText)}`} onClick={() => trackContact("email")}>Email sales@litpl.com</a>
            <a className="secondary-action" href={whatsappHref(fallbackText)} target="_blank" rel="noopener noreferrer" onClick={() => trackContact("whatsapp")}>Continue on WhatsApp</a>
          </div>
        </div>
      )}
    </form>
  );
}
